import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { money, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { ArrowLeft, FilePdf, Plus, Trash, FloppyDisk, PaperPlaneTilt, ClipboardText, CaretDown, ListChecks, Archive, ArrowClockwise, Prohibit, CreditCard } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import DateInput from "@/components/DateInput";
import { useAuth } from "@/context/AuthContext";

const emptyItem = () => ({ description: "", quantity: 1, unit_price: "", product_id: null });

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isOwner } = useAuth();
  const [state, setState] = useState(null);
  const [products, setProducts] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [sending, setSending] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidPassword, setVoidPassword] = useState("");
  const [voiding, setVoiding] = useState(false);
  const [payLoading, setPayLoading] = useState(false);

  const openCheckout = async () => {
    setPayLoading(true);
    try {
      const { data } = await api.post(`/invoices/${id}/checkout`);
      if (data?.url) {
        window.open(data.url, "_blank", "noopener");
        toast.success("Square checkout opened in a new tab");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not open Square checkout");
    } finally {
      setPayLoading(false);
    }
  };

  const load = () => api.get("/invoices/" + id).then((r) => setState(r.data));
  useEffect(() => {
    load();
    api.get("/products").then((r) => setProducts(r.data.filter((p) => (p.type || "product") !== "material")));
  }, [id]);

  if (!state) return <div className="text-slate-400">Loading…</div>;
  const { invoice: inv, customer } = state;

  const patch = (patchObj) => { setState((s) => ({ ...s, invoice: { ...s.invoice, ...patchObj } })); setDirty(true); };
  const updateItem = (idx, key, value) => {
    const items = [...(inv.items || [])];
    items[idx] = { ...items[idx], [key]: value };
    patch({ items });
  };
  const removeItem = (idx) => patch({ items: inv.items.filter((_, i) => i !== idx) });
  const addItem = () => patch({ items: [...(inv.items || []), emptyItem()] });
  const addFromProduct = (pid) => {
    const p = products.find((x) => x.id === pid);
    if (!p) return;
    patch({ items: [...(inv.items || []), { description: p.name, quantity: 1, unit_price: p.default_price, product_id: p.id }] });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        customer_id: inv.customer_id,
        work_order_id: inv.work_order_id,
        po_number: inv.po_number || "",
        items: (inv.items || []).map((i) => ({ ...i, quantity: Number(i.quantity) || 0, unit_price: Number(i.unit_price) || 0 })),
        tax_rate: Number(inv.tax_rate) || 0,
        discount: Number(inv.discount) || 0,
        discount_note: inv.discount_note || "",
        credit: Number(inv.credit) || 0,
        credit_note: inv.credit_note || "",
        amount_paid: Number(inv.amount_paid) || 0,
        payment_status: inv.payment_status,
        due_date: inv.due_date || null,
        notes: inv.notes || "",
      };
      const { data } = await api.put(`/invoices/${id}`, payload);
      setState((s) => ({ ...s, invoice: data }));
      setDirty(false);
      toast.success("Invoice saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Archive this invoice? The linked work order will also be archived. Both can be restored from the Archived tab.")) return;
    await api.post(`/invoices/${id}/archive`);
    toast.success("Archived"); navigate("/invoices");
  };

  const doVoid = async () => {
    if (!voidPassword) { toast.error("Enter your password to confirm"); return; }
    setVoiding(true);
    try {
      await api.post(`/invoices/${id}/void`, { password: voidPassword });
      toast.success("Invoice voided");
      setVoidOpen(false); setVoidPassword("");
      navigate("/invoices");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Void failed");
    } finally {
      setVoiding(false);
    }
  };

  const openEmail = () => {
    if (dirty) { toast.error("Save changes before sending"); return; }
    setEmailTo(customer?.email || "");
    setEmailOpen(true);
  };
  const sendEmail = async () => {
    if (!emailTo.trim()) { toast.error("Recipient email required"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/invoices/${id}/send`, { to: emailTo });
      toast.success(`Sent to ${data.to}`);
      setEmailOpen(false);
      // Refresh to pick up last_emailed metadata
      const r = await api.get(`/invoices/${id}`);
      setState(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send");
    } finally { setSending(false); }
  };

  const subtotal = (inv.items || []).reduce((s, i) => s + Number(i.quantity || 0) * Number(i.unit_price || 0), 0);
  const discount = Math.max(0, Number(inv.discount) || 0);
  const credit = Math.max(0, Number(inv.credit) || 0);
  const taxable = Math.max(0, subtotal - discount);
  const taxAmount = taxable * (Number(inv.tax_rate) || 0) / 100;
  const total = Math.max(0, taxable + taxAmount - credit);
  const balance = total - (Number(inv.amount_paid) || 0);

  const restore = async () => {
    await api.post(`/invoices/${id}/restore`);
    toast.success("Restored");
    load();
  };

  return (
    <div>
      <button onClick={() => navigate(-1)} className="text-slate-500 hover:text-slate-900 text-sm mb-3 flex items-center gap-1" data-testid="inv-back-btn">
        <ArrowLeft size={16} /> Back
      </button>
      {inv.voided && (
        <div className="mb-4 flex items-center gap-3 px-4 py-3 rounded-md" style={{ background: "#FCECE9", border: "1px solid #F3B2A6", color: "#A33522" }} data-testid="inv-voided-banner">
          <Prohibit size={18} weight="fill" />
          <div className="text-sm">
            <b>Voided</b>{inv.voided_by ? ` by ${inv.voided_by}` : ""}{inv.voided_at ? ` on ${fmtDate(inv.voided_at)}` : ""}. Balance removed from dashboards. This action cannot be reversed.
          </div>
        </div>
      )}
      {inv.archived && (
        <div className="mb-4 flex items-center justify-between gap-3 px-4 py-3 rounded-md" style={{ background: "#FEF5E6", border: "1px solid #FCE0B3", color: "#B36B00" }} data-testid="inv-archived-banner">
          <div className="text-sm">This invoice is <b>archived</b>. Restore to make it active again.</div>
          <button className="btn-ghost text-sm flex items-center gap-2" onClick={restore} data-testid="inv-restore-btn">
            <ArrowClockwise size={14} /> Restore
          </button>
        </div>
      )}
      <PageHeader
        title={inv.number}
        subtitle={`Invoice · ${customer?.name || ""}`}
        actions={
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="btn-ghost flex items-center gap-2" data-testid="inv-pdf-menu-trigger">
                  <FilePdf size={16} /> Print / Download <CaretDown size={12} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-52">
                <DropdownMenuItem asChild data-testid="inv-pdf-menu-invoice">
                  <a href={`${API_BASE}/invoices/${id}/pdf`} target="_blank" rel="noreferrer" className="flex items-center gap-2 w-full cursor-pointer">
                    <FilePdf size={14} /> Invoice PDF
                  </a>
                </DropdownMenuItem>
                {inv.work_order_id && (
                  <DropdownMenuItem asChild data-testid="inv-pdf-menu-wo">
                    <a href={`${API_BASE}/work-orders/${inv.work_order_id}/pdf`} target="_blank" rel="noreferrer" className="flex items-center gap-2 w-full cursor-pointer">
                      <ClipboardText size={14} /> Work Order PDF
                    </a>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild data-testid="inv-pdf-menu-packing">
                  <a href={`${API_BASE}/invoices/${id}/packing-slip`} target="_blank" rel="noreferrer" className="flex items-center gap-2 w-full cursor-pointer">
                    <ListChecks size={14} /> Packing Slip
                  </a>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <button className="btn-ghost flex items-center gap-2" onClick={openEmail} data-testid="inv-email-btn">
              <PaperPlaneTilt size={16} /> Email Invoice
            </button>
            <button className="btn-primary flex items-center gap-2" onClick={save} disabled={saving} data-testid="inv-save-btn">
              <FloppyDisk size={16} /> {saving ? "Saving…" : "Save Changes"}
            </button>
          </>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 surface p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline">Line Items</div>
              <p className="text-xs text-slate-400 mt-1">Click any field below to edit — description, quantity, unit price, totals, tax, discount, credit. Hit <span className="font-mono text-slate-700">Save Changes</span> when done.</p>
            </div>
            <div className="flex gap-2 items-center">
              <Select onValueChange={addFromProduct} value="">
                <SelectTrigger className="w-52" data-testid="inv-add-product-select"><SelectValue placeholder="Add from products…" /></SelectTrigger>
                <SelectContent>
                  {products.length === 0 && <div className="p-2 text-xs text-slate-400">No products yet. <Link to="/catalog" className="underline">Create some</Link>.</div>}
                  {products.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name} — {money(p.default_price)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button className="btn-ghost text-sm flex items-center gap-1" onClick={addItem} data-testid="inv-add-item-btn">
                <Plus size={14} /> Custom
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr><th>Description</th><th className="text-right">Qty</th><th className="text-right">Unit</th><th className="text-right">Total</th><th></th></tr>
              </thead>
              <tbody>
                {(inv.items || []).length === 0 && (
                  <tr><td colSpan={5} className="text-center text-slate-400 py-8">No line items. Add from products or a custom line.</td></tr>
                )}
                {(inv.items || []).map((it, idx) => (
                  <tr key={idx} className="hover:bg-transparent cursor-default">
                    <td className="min-w-[240px] align-top">
                      <Textarea
                        rows={2}
                        value={it.description}
                        onChange={(e) => updateItem(idx, "description", e.target.value)}
                        data-testid={`inv-item-desc-${idx}`}
                        className="resize-y font-medium leading-snug"
                        placeholder="Item name&#10;specs / description"
                      />
                    </td>
                    <td className="w-24">
                      <Input type="number" step="0.5" min="0" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} onFocus={(e) => e.target.select()} className="text-right font-mono" data-testid={`inv-item-qty-${idx}`} />
                    </td>
                    <td className="w-32">
                      <Input type="number" step="0.01" min="0" value={it.unit_price ?? ""} onChange={(e) => updateItem(idx, "unit_price", e.target.value)} onFocus={(e) => e.target.select()} className="text-right font-mono" data-testid={`inv-item-price-${idx}`} placeholder="0.00" />
                    </td>
                    <td className="text-right font-mono w-28">{money(Number(it.quantity || 0) * Number(it.unit_price || 0))}</td>
                    <td className="w-10">
                      <button className="text-slate-400 hover:text-red-400" onClick={() => removeItem(idx)} data-testid={`inv-item-remove-${idx}`}>
                        <Trash size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs text-slate-600">PO # (Purchase Order)</Label>
              <Input
                data-testid="inv-po-number"
                value={inv.po_number || ""}
                onChange={(e) => patch({ po_number: e.target.value })}
                placeholder="Optional — shows on all PDFs"
                className="font-mono"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-slate-600">Notes (visible on invoice PDF)</Label>
              <Textarea rows={3} value={inv.notes || ""} onChange={(e) => patch({ notes: e.target.value })} data-testid="inv-notes" />
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div className="surface p-5">
            <div className="overline mb-3">Totals</div>
            <Row label="Subtotal" value={money(subtotal)} />

            <div className="py-1.5 border-t hairline mt-2">
              <div className="flex items-center justify-between text-sm text-slate-800 py-1">
                <span>Discount</span>
                <div className="flex items-center gap-1">
                  <span className="text-slate-500">$</span>
                  <Input className="w-24 text-right font-mono h-8" type="number" step="0.01" min="0" value={inv.discount || 0}
                    onChange={(e) => patch({ discount: e.target.value })} data-testid="inv-discount" />
                </div>
              </div>
              <Input className="h-8 text-xs" placeholder="Discount reason (optional)" value={inv.discount_note || ""}
                onChange={(e) => patch({ discount_note: e.target.value })} data-testid="inv-discount-note" />
            </div>

            <div className="flex items-center justify-between text-sm text-slate-800 py-1.5">
              <span>Tax Rate</span>
              <div className="flex items-center gap-2">
                <Input className="w-20 text-right font-mono h-8" type="number" step="0.01" min="0" value={inv.tax_rate} onChange={(e) => patch({ tax_rate: e.target.value })} data-testid="inv-tax-rate" />
                <span className="text-slate-500">%</span>
              </div>
            </div>
            <Row label="Tax" value={money(taxAmount)} />

            <div className="py-1.5 border-t hairline mt-2">
              <div className="flex items-center justify-between text-sm text-slate-800 py-1">
                <span>Credit</span>
                <div className="flex items-center gap-1">
                  <span className="text-slate-500">$</span>
                  <Input className="w-24 text-right font-mono h-8" type="number" step="0.01" min="0" value={inv.credit || 0}
                    onChange={(e) => patch({ credit: e.target.value })} data-testid="inv-credit" />
                </div>
              </div>
              <Input className="h-8 text-xs" placeholder="Credit reason (optional)" value={inv.credit_note || ""}
                onChange={(e) => patch({ credit_note: e.target.value })} data-testid="inv-credit-note" />
            </div>

            <div className="border-t hairline my-2" />
            <Row label="Total" value={money(total)} bold />
            <Row label="Amount Paid" value={money(inv.amount_paid || 0)} />
            <div className="border-t hairline my-2" />
            <Row label="Balance Due" value={money(balance)} accent bold />
          </div>

          <div className="surface p-5">
            <div className="overline mb-3">Payment</div>
            <Label className="text-xs text-slate-600">Amount Paid</Label>
            <Input type="number" step="0.01" min="0" value={inv.amount_paid || 0} onChange={(e) => patch({ amount_paid: e.target.value })} data-testid="inv-amount-paid" />
            <div className="mt-3">
              <Label className="text-xs text-slate-600">Status</Label>
              <Select value={inv.payment_status} onValueChange={(v) => patch({ payment_status: v })}>
                <SelectTrigger data-testid="inv-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unpaid">Unpaid</SelectItem>
                  <SelectItem value="partial">Partial</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="mt-3">
              <Label className="text-xs text-slate-600">Due Date</Label>
              <DateInput value={inv.due_date || ""} onChange={(e) => patch({ due_date: e.target.value })} data-testid="inv-due-date" />
            </div>
            <button className="btn-ghost w-full mt-3 text-sm" onClick={() => patch({ amount_paid: total, payment_status: "paid" })} data-testid="inv-mark-paid">
              Mark Fully Paid
            </button>
            <SquarePayBlock invoice={inv} onCheckout={openCheckout} loading={payLoading} />
          </div>

          <div className="surface p-5">
            <div className="overline mb-2">Customer</div>
            <Link to={`/customers/${customer?.id}`} className="text-slate-900 font-medium hover:underline">{customer?.name}</Link>
            <div className="text-sm text-slate-500 mt-1">{customer?.company}</div>
            <div className="text-xs font-mono text-slate-400 mt-2">{customer?.email}</div>
            <div className="text-xs font-mono text-slate-400">{customer?.phone}</div>
          </div>

          {inv.work_order_id && (
            <div className="surface p-5" data-testid="inv-linked-wo-card">
              <div className="overline mb-2">Linked Work Order</div>
              <p className="text-xs text-slate-500 mb-3">Handed to the shop floor. No prices are shown on work orders.</p>
              <Link to={`/work-orders/${inv.work_order_id}`} className="btn-ghost text-sm text-center block" data-testid="inv-goto-wo">
                View / Edit Work Order →
              </Link>
            </div>
          )}

          {isOwner && !inv.voided && (
            <button className="btn-ghost text-red-500 hover:text-red-600 flex items-center justify-center gap-2" onClick={() => setVoidOpen(true)} data-testid="inv-void-btn">
              <Prohibit size={16} /> Void Invoice
            </button>
          )}
        </div>
      </div>

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="max-w-md" data-testid="inv-email-dialog">
          <DialogHeader><DialogTitle>Email Invoice {inv.number}</DialogTitle></DialogHeader>
          <div className="py-2 space-y-3">
            <div>
              <Label className="text-xs text-slate-600">Send to</Label>
              <Input type="email" data-testid="inv-email-to" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="customer@email.com" />
              <p className="text-xs text-slate-400 mt-1">The invoice PDF will be attached automatically.</p>
            </div>
            {inv.last_emailed_at && (
              <div className="text-xs text-slate-500 border hairline p-2">
                Last sent: <span className="font-mono">{new Date(inv.last_emailed_at).toLocaleString()}</span> to <span className="font-mono">{inv.last_emailed_to}</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <button className="btn-ghost" onClick={() => setEmailOpen(false)}>Cancel</button>
            <button className="btn-primary flex items-center gap-2" onClick={sendEmail} disabled={sending} data-testid="inv-email-send">
              <PaperPlaneTilt size={16} /> {sending ? "Sending…" : "Send"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={voidOpen} onOpenChange={setVoidOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600"><Prohibit size={22} weight="fill" /> Void Invoice</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <div className="text-sm text-slate-700 leading-relaxed">
              Voiding <span className="font-mono font-semibold">{inv.number}</span> is <b>permanent</b>. This will:
            </div>
            <ul className="text-sm text-slate-700 space-y-1 pl-4 list-disc">
              <li>Remove the money owed (${(Number(inv.total) || 0).toFixed(2)}) from all dashboards</li>
              <li>Unlink the associated work order (the WO stays alive for a new invoice)</li>
              <li>Cannot be undone — invoice will not reopen</li>
            </ul>
            <div>
              <Label className="text-xs text-slate-600">Enter your owner password to confirm</Label>
              <Input
                type="password"
                autoFocus
                value={voidPassword}
                onChange={(e) => setVoidPassword(e.target.value)}
                data-testid="inv-void-password"
                placeholder="Your password"
              />
            </div>
          </div>
          <DialogFooter>
            <button className="btn-ghost" onClick={() => { setVoidOpen(false); setVoidPassword(""); }}>Cancel</button>
            <button
              className="btn-primary bg-red-600 hover:bg-red-700 flex items-center gap-2"
              style={{ background: "#DC2626" }}
              onClick={doVoid}
              disabled={voiding || !voidPassword}
              data-testid="inv-void-confirm"
            >
              <Prohibit size={16} /> {voiding ? "Voiding…" : "Void Permanently"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Row = ({ label, value, bold, accent }) => (
  <div className={`flex items-center justify-between py-1.5 text-sm ${bold ? "font-semibold" : ""}`}>
    <span className={accent ? "text-emerald-300" : "text-slate-600"}>{label}</span>
    <span className={`font-mono ${accent ? "text-emerald-300" : "text-slate-900"}`}>{value}</span>
  </div>
);

function SquarePayBlock({ invoice, onCheckout, loading }) {
  const balance = (Number(invoice.total) || 0) - (Number(invoice.amount_paid) || 0);
  const disabled = invoice.voided || balance <= 0 || loading;
  return (
    <div className="mt-4 pt-4 border-t hairline">
      <div className="overline mb-2 flex items-center gap-1"><CreditCard size={12} /> Online Payment</div>
      <button
        className="btn-primary w-full text-sm flex items-center justify-center gap-2"
        onClick={onCheckout}
        disabled={disabled}
        data-testid="inv-square-pay-btn"
      >
        <CreditCard size={14} />
        {loading ? "Opening…" : `Pay ${(balance).toFixed(2)} with Square`}
      </button>
      {invoice.square_checkout_url && !invoice.voided && (
        <a
          href={invoice.square_checkout_url}
          target="_blank"
          rel="noreferrer"
          className="text-[11px] text-slate-500 hover:text-emerald-700 mt-2 block font-mono truncate"
          data-testid="inv-square-existing-link"
        >
          Reopen existing link → {invoice.square_checkout_url.replace("https://", "")}
        </a>
      )}
      <p className="text-[11px] text-slate-400 mt-2">
        Customer pays by card, Apple Pay, or Cash App on Square&apos;s hosted page. The invoice auto-marks paid once the payment clears.
      </p>
    </div>
  );
}
